import { Card, CardContent, Grid, Stack } from '@mui/joy';
import { PencilSimple } from '@phosphor-icons/react';
import { SyntheticEvent, useEffect, useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ContactFormFields, UpsertAddressFormFields } from 'addresses/components';
import { AddressFields } from 'addresses/enums';
import {
  BillToLocationFormField,
  LocationFormField,
  ShipToLocationFormField,
} from 'contacts/elements';
import { IContactGroup, IContactGroupAddress } from 'contacts/types';
import { Button, Form, FormField, Input } from 'core/components';
import { FormSectionHeader } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { IAutocompleteOption } from 'core/types';
import { corporateUserService } from 'corporate/services';
import { ICorporateUserAutocomplete } from 'corporate/types';
import { Color, Direction, Size, Variant } from 'theme/enums';

import { AddressDetails } from './AddressDetails';
import { ContactDetails } from './ContactDetails';
import { ContactGroupAddressType } from './enums';
import { IQuoteAddressForm } from './types';
import { getAccountsReceivableId, getContactGroupAddressTitle } from './utils';

interface IProps {
  isOpen: boolean;
  onClose?: (reset?: UseFormReset<IQuoteAddressForm>) => void;
  onSubmit?: (reset: UseFormReset<IQuoteAddressForm>) => (data: IQuoteAddressForm) => void;
  type: ContactGroupAddressType;
  onChangeLocation: (
    reset: UseFormReset<IQuoteAddressForm>,
    data: IQuoteAddressForm,
    option: IContactGroupAddress
  ) => void;
  onChangeContact: (
    reset: UseFormReset<IQuoteAddressForm>,
    data: IQuoteAddressForm,
    option: ICorporateUserAutocomplete
  ) => void;
  customerContactGroup?: IContactGroup;
  locationRequestPayload?: Record<string, Record<string, unknown>>;
  contactRequestPayload?: Record<string, unknown>;
  hasContactFields?: boolean;
  isDefaultEditing?: boolean;
  hasCreateNewLocation?: boolean;
  hasCreateNewContact?: boolean;
  pickedAccountsReceivableId?: string;
  isAlternateVersionEnabled?: boolean;
  requiredAddressFields?: AddressFields[];
  disableArNumberFilterToggle?: boolean;
}

export const UpdateAddressByTypeFormFields = ({
  isOpen,
  onClose,
  onSubmit,
  type,
  onChangeLocation,
  onChangeContact,
  customerContactGroup,
  locationRequestPayload,
  contactRequestPayload,
  hasContactFields,
  isDefaultEditing = false,
  hasCreateNewLocation = false,
  hasCreateNewContact = false,
  pickedAccountsReceivableId,
  isAlternateVersionEnabled,
  requiredAddressFields,
  disableArNumberFilterToggle = false,
}: IProps) => {
  const [isEditingLocation, setIsEditingLocation] = useState<boolean>(isDefaultEditing);
  const [isEditingContact, setIsEditingContact] = useState<boolean>(isDefaultEditing);

  const { t } = useTranslation();
  const { reset, watch, getValues } = useFormContext();

  useEffect(() => {
    if (isOpen) {
      setIsEditingLocation(isDefaultEditing);
      setIsEditingContact(isDefaultEditing);
    }
  }, [isOpen, isDefaultEditing]);

  const LocationField = (() => {
    switch (type) {
      case ContactGroupAddressType.BILL_TO:
        return BillToLocationFormField;
      case ContactGroupAddressType.SHIP_TO:
        return ShipToLocationFormField;
      default:
        return LocationFormField;
    }
  })();

  return (
    <Form
      title={getContactGroupAddressTitle(type)}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
    >
      <FormSectionHeader title={t('titleLocation')} />

      <LocationField
        name="location"
        contactGroup={customerContactGroup}
        requestPayload={locationRequestPayload}
        onChange={(_: SyntheticEvent, option: IContactGroupAddress | IContactGroupAddress[]) => {
          setIsEditingLocation(false);
          onChangeLocation(reset, getValues(), option as IContactGroupAddress);
        }}
        hasCreateOption={hasCreateNewLocation}
        onAddNewOption={(location: IAutocompleteOption) => {
          setIsEditingLocation(true);
          onChangeLocation(reset, getValues(), { name: location.title } as IContactGroupAddress);
        }}
        groupOptionsByContactGroup={Boolean(
          isAlternateVersionEnabled &&
            customerContactGroup &&
            locationRequestPayload?.viewBy?.contactGroupId &&
            locationRequestPayload?.viewBy?.includeOtherContactGroups
        )}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        disableArNumberFilterToggle={disableArNumberFilterToggle}
      />

      {!isEditingLocation && <AddressDetails address={watch('location')} />}

      {isEditingLocation && (
        <>
          <UpsertAddressFormFields fieldsPrefix="address" requiredFields={requiredAddressFields} />

          {type === ContactGroupAddressType.BILL_TO && isAlternateVersionEnabled && (
            <Input
              value={getAccountsReceivableId(pickedAccountsReceivableId, watch('location'))}
              placeholder={t('labelAccountNumber')}
              label={t('labelAccountNumber')}
              disabled={true}
            />
          )}
        </>
      )}

      {hasContactFields && (
        <>
          <FormSectionHeader title={t('titleContact')} />

          <FormField
            name="contact"
            type={InputType.AUTOCOMPLETE}
            inputProps={{
              placeholder: t('placeholderContact'),
              getOptions: corporateUserService.getCorporateForeignUsersAutocomplete,
              requestPayload: contactRequestPayload,
              getLabel: (option: ICorporateUserAutocomplete) =>
                option.companyName ? `${option.name} (${option.companyName})` : option.name,
              onChange: (_: SyntheticEvent, option: ICorporateUserAutocomplete) => {
                setIsEditingContact(false);
                onChangeContact(reset, getValues(), option);
              },
              disableClearable: true,
              hasCreateOption: hasCreateNewContact,
              onNewOption: (option: IAutocompleteOption) => {
                setIsEditingContact(true);
                onChangeContact(reset, getValues(), {
                  name: option.title,
                } as ICorporateUserAutocomplete);
              },
            }}
          />

          <Card variant={Variant.PLAIN}>
            <CardContent>
              <Grid container>
                <Grid xs={12} md={10}>
                  <Stack direction={Direction.COLUMN} gap={1}>
                    {!isEditingContact && <ContactDetails quoteAddress={watch()} />}

                    {isEditingContact && <ContactFormFields />}
                  </Stack>
                </Grid>

                <Grid
                  xs={12}
                  md={2}
                  sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}
                >
                  <Button
                    color={Color.PRIMARY}
                    size={Size.SM}
                    variant={Variant.PLAIN}
                    icon={<PencilSimple />}
                    onClick={() => setIsEditingContact((prev) => !prev)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}
    </Form>
  );
};
